import { Badge, Button, Text } from '@fluentui/react-components';
import {
  CheckmarkCircle16Filled,
  Delete16Regular,
  Edit16Regular,
} from '@fluentui/react-icons';

import {
  ENGAGEMENT_TYPES,
  SCENARIO_LIBRARY,
} from './expectedAction.constants.js';
import type { ExpectedActionSummaryProps } from './ExpectedActionSummary.interface.js';
import { useStyles } from './ExpectedActionSummary.style.js';

const TITLE_LABEL = 'Expected action';
const EDIT_LABEL = 'Edit';
const DELETE_LABEL = 'Delete';
const ENGAGEMENT_ROW_LABEL = 'Engagement';
const INTERACTION_ROW_LABEL = 'Interaction';
const SCENARIOS_ROW_LABEL = 'Scenarios';
const CUSTOMIZATION_ROW_LABEL = 'Customization asks';
const EMPTY_SUMMARY_LABEL = 'No details captured yet';

interface SummaryRowProps {
  label: string;
  values: readonly string[];
}

export function ExpectedActionSummary({
  draft,
  onEdit,
  onDelete,
  engagementTypes = ENGAGEMENT_TYPES,
  scenarios = SCENARIO_LIBRARY,
}: ExpectedActionSummaryProps) {
  const styles = useStyles();

  const engagementNames = draft.engagementInputs.map(
    (entry) =>
      engagementTypes.find((type) => type.id === entry.type)?.name ??
      entry.type,
  );
  const scenarioNames = draft.associatedScenarios.map(
    (id) => scenarios.find((scenario) => scenario.id === id)?.name ?? id,
  );

  const hasAnyDetail =
    engagementNames.length > 0 ||
    draft.interactionTypes.length > 0 ||
    scenarioNames.length > 0 ||
    draft.associatedCustomizationAsks.length > 0;

  const renderRow = ({ label, values }: SummaryRowProps) =>
    values.length > 0 ? (
      <div className={styles.summaryRow}>
        <Text size={200} weight="semibold" className={styles.rowLabel}>
          {label}
        </Text>
        <div className={styles.rowValues}>
          {values.map((value) => (
            <Badge key={value} appearance="tint" color="informative">
              {value}
            </Badge>
          ))}
        </div>
      </div>
    ) : null;

  return (
    <section className={styles.root} aria-label={TITLE_LABEL}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <CheckmarkCircle16Filled className={styles.confirmedIcon} />
          <Text weight="semibold">{TITLE_LABEL}</Text>
        </div>
        <div className={styles.headerActions}>
          <Button
            appearance="subtle"
            size="small"
            icon={<Edit16Regular />}
            onClick={onEdit}
          >
            {EDIT_LABEL}
          </Button>
          <Button
            appearance="subtle"
            size="small"
            icon={<Delete16Regular />}
            onClick={onDelete}
          >
            {DELETE_LABEL}
          </Button>
        </div>
      </div>

      <div className={styles.body}>
        {hasAnyDetail ? (
          <>
            {renderRow({
              label: ENGAGEMENT_ROW_LABEL,
              values: engagementNames,
            })}
            {renderRow({
              label: INTERACTION_ROW_LABEL,
              values: draft.interactionTypes,
            })}
            {renderRow({ label: SCENARIOS_ROW_LABEL, values: scenarioNames })}
            {renderRow({
              label: CUSTOMIZATION_ROW_LABEL,
              values: draft.associatedCustomizationAsks,
            })}
          </>
        ) : (
          <Text size={200} className={styles.emptyText}>
            {EMPTY_SUMMARY_LABEL}
          </Text>
        )}
      </div>
    </section>
  );
}
